import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Logout as LogoutIcon } from '@mui/icons-material';
import Summary from './components/Summary';
import GridTabs from './components/GridTabs';
import SaveBar from './components/SaveBar';
import Login from './components/Login';
import AuthError from './components/AuthError';
import MeetingPage from './components/MeetingPage';
import NotFound from './components/NotFound';
import Home from './components/Home';
import CascadeIcon from './components/CascadeIcon';
import { useAuth } from './hooks/useAuth';
import { tokenRenewalService } from './services/tokenRenewal';
import { logDebug } from './utils/errorLogger';
import { useAppStore } from './state/store';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    // DataGrid styling will be handled in individual components
  },
});

const MeetingPageWrapper: React.FC = () => {
  const { isLoading } = useAppStore();
  
  return (
    <Box sx={{ p: 3 }}>
      <MeetingPage />
      {!isLoading && (
        <>
          <SaveBar />
          <Summary />
          <GridTabs />
        </>
      )}
    </Box>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();

  // Start/stop token renewal based on authentication status
  useEffect(() => {
    if (isAuthenticated) {
      tokenRenewalService.startRenewal();
    } else {
      tokenRenewalService.stopRenewal();
    }

    // Cleanup on unmount
    return () => {
      tokenRenewalService.stopRenewal();
    };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
    // Don't navigate - let the auth state change trigger the UI update naturally
    // This ensures the URL shows / instead of /home
  };

  const handleHome = () => {
    navigate('/home');
  };

  // Data loading is now handled by individual meeting pages
  // No auto-loading at app level

  // Show loading spinner during auth check
  if (isLoading) {
    logDebug('App is in loading state...');
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography>Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Handle unauthenticated users based on current route
  if (!isAuthenticated) {
    const currentPath = window.location.pathname;
    logDebug('User not authenticated, current path:', currentPath);
    
    // Show auth error page when authentication fails
    if (currentPath === '/auth-error' || currentPath.startsWith('/auth-error?')) {
      return (
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CssBaseline />
            <AuthError />
          </LocalizationProvider>
        </ThemeProvider>
      );
    }

    // Show login page for root, login, home, or meeting routes when not authenticated
    if (currentPath === '/' || currentPath === '/login' || currentPath === '/home' || currentPath.startsWith('/meeting/')) {
      // Determine where to redirect after login
      const getReturnUrl = () => {
        if (currentPath === '/' || currentPath === '/login') {
          return '/home'; // Redirect to home for base routes
        }
        return currentPath; // Redirect to the original path for specific routes
      };
      
      const returnUrl = getReturnUrl();
      logDebug('Showing login page with returnUrl:', returnUrl);
      
      return (
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CssBaseline />
            <Login 
              onLogin={(success) => success && (window.location.href = returnUrl)} 
              returnUrl={returnUrl}
            />
          </LocalizationProvider>
        </ThemeProvider>
      );
    }
    
    // Show 404 for any other route when not authenticated (no blue banner)
    return (
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CssBaseline />
          <NotFound />
        </LocalizationProvider>
      </ThemeProvider>
    );
  }

  logDebug('User authenticated, rendering main app');
  
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
          <AppBar position="static" elevation={1}>
            <Toolbar>
              <Box
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  flexGrow: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
                onClick={handleHome}
              >
                <CascadeIcon sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" component="div">
                  Cascade
                </Typography>
              </Box>
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            </Toolbar>
          </AppBar>

          <Routes>
            {/* Root route - redirect to home */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            
            {/* Login route - redirect to home (user is already authenticated) */}
            <Route path="/login" element={<Navigate to="/home" replace />} />
            
            {/* Home route */}
            <Route path="/home" element={<Home />} />
            
            {/* Meeting route */}
            <Route path="/meeting/:meetingId" element={<MeetingPageWrapper />} />
            
            {/* Auth error route */}
            <Route path="/auth-error" element={<AuthError />} />
            
            {/* Explicit 404 route */}
            <Route path="/404" element={<NotFound />} />
            
            {/* All other routes show 404 with blue banner */}
            <Route path="*" element={<NotFound />} />
          </Routes>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
